import * as _ from 'lodash-es';
import { createSelector } from 'reselect';
import { uploadErrorType } from '../Constants/uploadErrorType';
import {
  LCA_ALLOWED_EXTENSIONS,
  DIGITAL_ARCHIVE_MODULE_CODE,
} from '../Utils/constants';
import { getFileExtension } from '../Utils/fileUtils';

export const getUploadOptions = (state) => state.upload.options;
export const getUploadFiles = (state) => state.upload.files;
export const getProjectCode = (state) => state.project?.openedProjectCode;

export const getUploadOptionsDisabled = createSelector(
  getUploadFiles,
  (files) => files.some((f) => f.parentFileName != null)
);

export const getExistingDocuments = (state) => state.upload.existingDocuments;
export const getDuplicates = (state) => state.upload.duplicates;

const isDigitalArchiveModule = (projectCode) =>
  projectCode === DIGITAL_ARCHIVE_MODULE_CODE;

const isInvalidFileType = (fileName, projectCode) =>
  isDigitalArchiveModule(projectCode) &&
  !LCA_ALLOWED_EXTENSIONS.includes(getFileExtension(fileName)?.toLowerCase());

export const getUploadErrors = createSelector(
  [getExistingDocuments, getDuplicates, getUploadFiles, getProjectCode],
  (existingDocuments, duplicatesMap, files, projectCode) => {
    const allowedFiles = [];
    const invalidFiles = [];

    files.forEach((f) => {
      if (isInvalidFileType(f.name, projectCode)) {
        invalidFiles.push({ ...f, type: uploadErrorType.INVALID_FILE_TYPE });
      } else {
        allowedFiles.push(f);
      }
    });

    const filesMap = _.keyBy(allowedFiles, (f) => `${f.name}_${f.path || ''}`);

    if (!existingDocuments) {
      return { existingDocuments: [], duplicateErrors: [], invalidFiles };
    }

    const existingDocs = existingDocuments.map((ed) => ({
      ...ed,
      type: filesMap[`${ed.name}_${ed.path || ''}`]?.reference
        ? uploadErrorType.REFERENCE
        : ed.type,
    }));

    const duplicateErrors = Object.entries(duplicatesMap)
      .map(([hash, dupFiles]) => {
        const filtered = (dupFiles || []).filter((df) =>
          allowedFiles.some((af) => af.fileIdentifier === df.fileIdentifier)
        );
        return {
          type: uploadErrorType.FILE_CONTENT_DUPLICATE,
          hash,
          files: filtered,
        };
      })
      .filter((e) => e.files && e.files.length > 0);

    return { existingDocuments: existingDocs, duplicateErrors, invalidFiles };
  }
);

export const getUploadFilesWithErrors = createSelector(
  [getExistingDocuments, getDuplicates, getUploadFiles, getProjectCode],
  (existingDocuments, duplicatesMap, files, projectCode) =>
    files.map((f) => {
      const invalidType =
        isInvalidFileType(f.name, projectCode) &&
        uploadErrorType.INVALID_FILE_TYPE;

      const existingDocumentsForFile = existingDocuments.filter(
        (ed) => ed.name === f.name && ed.path === f.path
      );
      const duplicatesForFile = Object.entries(duplicatesMap)
        .filter(([_, dupFiles]) =>
          dupFiles.some(
            (file) =>
              file.fileIdentifier === f.fileIdentifier &&
              !isInvalidFileType(file.name, projectCode)
          )
        )
        .map(([hash, dupFiles]) => ({
          type: uploadErrorType.FILE_CONTENT_DUPLICATE,
          hash,
          files: dupFiles,
        }));

      const errors = [
        ...existingDocumentsForFile,
        ...duplicatesForFile,
        ...(invalidType ? [{ type: invalidType }] : []),
      ];

      return { ...f, errors };
    })
);
